/* eslint-disable prettier/prettier */
import React from 'react'
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { showToast, hideToast } from './toastSlice'
import makeRequest from 'src/services/api'
import handleGeneralError from '../helpers/errorHandler'
import {
  fetchProductMasterDataUrl,
  addProductMasterDataUrl,
  editProductMasterDataUrl,
  deleteProductMasterDataUrl,
} from 'src/services/urls'
import { setSearchBar } from './tableSlice'

const initialState = {
  loading: false,
  data: { hasMore: false, page: 1, totalDocs: 0, nextPage: 0 },
  docs: [],
  error: '',
  activePage: 0,
}

// Fetch product master data
export const fetchProductMasterData = createAsyncThunk(
  'productMasterData/fetchProductMasterData',
  async (options, { dispatch }) => {
    try {
      const skip = options?.skip
      const limit = options?.limit
      const productName = options?.productName
      const brandName = options?.brandName
      const sortBy = options?.sortBy
      const orderBy = options?.orderBy

      const response = await makeRequest(
        fetchProductMasterDataUrl(skip, limit, productName, brandName, sortBy, orderBy),
      )
      let docs = []
      if (response.data) {
        docs = response.data.data.docs.map((item, i) => {
          return { ...item, srno: i + 1 }
        })
      }
      return { docs, data: response.data.data }
    } catch (e) {
      handleGeneralError(e, { dispatch, showToast })
      return { error: e?.response?.data?.error?.message }
    }
  },
)

// Add a product master data manually - no upload widget
export const addProductMasterData = createAsyncThunk(
  'productMasterData/addProductMasterData',
  async (data, { dispatch }) => {
    const body = { ...data }
    const filterProductName = data?.searchProductName
    delete body.searchProductName
    delete body._id
    try {
      const response = await makeRequest(addProductMasterDataUrl(), 'POST', body)
      const { displayMessage, statusMessage } = response.data

      if (response.data.statusCode === 201) {
        dispatch(showToast({ title: statusMessage, message: displayMessage, color: 'success' }))
      } else {
        dispatch(showToast({ title: 'Failed', message: displayMessage, color: 'danger' }))
      }
      dispatch(fetchProductMasterData({ productName: filterProductName }))
      dispatch(setSearchBar(filterProductName))
      return response.data
    } catch (e) {
      if (e?.response?.status === 500)
        dispatch(
          showToast({
            title: e?.response?.data?.status,
            message: e?.response?.data?.error?.message,
            color: 'danger',
          }),
        )
      else if (e.response.data.statusCode === 409) {
        const handleEnable = () => {
          dispatch(editProductMasterData(body))
          dispatch(hideToast())
        }
        const productId = e.response.data.error.productId
        if (productId) {
          body.changeScope = true
          body._id = productId
          body.inScope = true
          dispatch(
            showToast({
              title: e.response.data.statusMessage,
              message: e.response.data.displayMessage,
              color: 'warning',
              action: true,
              actionLabel: 'Enable',
              actionHandler: handleEnable,
            }),
          )
        } else {
          dispatch(
            showToast({
              title: e.response.data.statusMessage,
              message: e.response.data.displayMessage,
              color: 'warning',
            }),
          )
        }
      } else
        dispatch(
          showToast({
            title: e.response.data.displayMessage,
            message: e.response.data.error.map((e) => <p key={e.message}>{e.message}</p>),
            color: 'danger',
          }),
        )
    }
  },
)

// Edit/Update or enable/disable a product master data
export const editProductMasterData = createAsyncThunk(
  'productMasterData/editProductMasterData',
  async (data, { dispatch }) => {
    try {
      let body = {
        productName: data.productName,
        brandId: data.brandId,
        stateId: data.stateId,
        comments: data.comments,
        b_uom_size: data.b_uom_size,
        base_uom: data.base_uom,
        recommendedPoints: data.recommendedPoints,
      }
      const id = data._id
      if (data.productName === undefined || data.changeScope) body.inScope = data.inScope
      if ('comments' in data) body.comments = data.comments
      const response = await makeRequest(editProductMasterDataUrl(id), 'PUT', body)

      const { displayMessage, status } = response.data
      if (response.data.statusCode === 200) {
        dispatch(showToast({ title: status, message: displayMessage, color: 'success' }))
      }
      dispatch(fetchProductMasterData({ productName: data.searchProductName }))
      dispatch(setSearchBar(data.searchProductName))
      return response.data
    } catch (e) {
      handleGeneralError(e, { dispatch, showToast })
    }
  },
)

// Delete a product master data
export const deleteProductMasterData = createAsyncThunk(
  'productMasterData/deleteProductMasterData',
  async (id, { dispatch }) => {
    try {
      const response = await makeRequest(deleteProductMasterDataUrl(id), 'DELETE')
      const { displayMessage, status } = response.data

      if (response.data.statusCode === 200) {
        dispatch(showToast({ title: status, message: displayMessage, color: 'success' }))
      }

      dispatch(fetchProductMasterData())

      return response.data
    } catch (e) {
      handleGeneralError(e, { dispatch, showToast })
    }
  },
)

const productMasterDataSlice = createSlice({
  name: 'productMasterData',
  initialState,
  reducers: {
    setActivePage: (state, action) => {
      state.activePage = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchProductMasterData.pending, (state) => {
      state.loading = true
    })
    builder.addCase(fetchProductMasterData.fulfilled, (state, action) => {
      state.loading = false
      state.error = action.payload.error
      if (!action.payload.error) {
        state.data = action.payload.data
        state.docs = action.payload.docs
      }
    })
    builder.addCase(fetchProductMasterData.rejected, (state, action) => {
      state.loading = false
      state.data = {}
      state.docs = []
      state.error = action.error.message
    })
    builder.addCase(addProductMasterData.fulfilled, (state, action) => {
      state.loading = false
      state.error = ''
    })
    builder.addCase(addProductMasterData.rejected, (state, action) => {
      state.loading = false
      state.error = action.error.message
    })
    builder.addCase(editProductMasterData.fulfilled, (state, action) => {
      state.loading = false
      state.error = ''
    })
    builder.addCase(editProductMasterData.rejected, (state, action) => {
      state.loading = false
      state.error = action.error.message
    })
    builder.addCase(deleteProductMasterData.fulfilled, (state, action) => {
      state.loading = false
      state.error = ''
    })
    builder.addCase(deleteProductMasterData.rejected, (state, action) => {
      state.loading = false
      state.error = action.error.message
    })
  },
})

export default productMasterDataSlice.reducer
export const { setActivePage } = productMasterDataSlice.actions
